'use client';

import { ChevronDown } from 'lucide-react';
import type { CountryOption } from '@/lib/country-list';
import { useRef, useState, type FormEvent } from 'react';
import { Loader } from '@/components/ui/loader';
import type { MarketingContactCopy } from '@/i18n/marketing-contact-copy';
import {
  BUSINESS_CONTACT_MESSAGE_MAX_LENGTH,
  businessContactSchema,
} from '@/lib/contact/contact-message';
import {
  submitContactForm,
  type ContactFormResult,
} from '@/lib/contact/submit-contact-form';
import { cn } from '@/lib/utils';
import { MarketingCtaLabel } from './marketing-cta-label';

const fieldClass =
  'border-marketing-border bg-marketing-bg text-marketing-fg focus-visible:outline-marketing-accent-strong mt-2 min-h-12 w-full appearance-none rounded-md border px-4 py-3 text-base outline-none focus-visible:outline-2 focus-visible:outline-offset-2';

export function ContactForm({
  copy,
  countries,
}: {
  copy: MarketingContactCopy;
  countries: CountryOption[];
}) {
  const pending = useRef(false);
  const session = useRef<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<ContactFormResult | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending.current) return;
    const form = event.currentTarget;
    const data = new FormData(form);
    if (!session.current) {
      session.current = `contact_${crypto.randomUUID()}`;
      try {
        session.current =
          sessionStorage.getItem('marketingContactSessionId') ??
          session.current;
        sessionStorage.setItem('marketingContactSessionId', session.current);
      } catch {
        // Storage can be blocked; keep the same session for this mounted form.
      }
    }
    const parsed = businessContactSchema.safeParse({
      formType: 'business',
      firstName: data.get('firstName'),
      lastName: data.get('lastName'),
      email: data.get('email'),
      company: data.get('company'),
      jobTitle: data.get('jobTitle'),
      phone: data.get('phone'),
      message: data.get('message'),
      country: data.get('country'),
      marketingConsent: data.get('marketingConsent') === 'on',
      sessionId: session.current,
    });
    if (!parsed.success) {
      setResult('invalid');
      return;
    }
    pending.current = true;
    setIsSubmitting(true);
    setResult(null);
    const nextResult = await submitContactForm(parsed.data, session.current);
    setResult(nextResult);
    if (nextResult === 'success') form.reset();
    pending.current = false;
    setIsSubmitting(false);
  }

  return (
    <div className="bg-marketing-surface rounded-2xl p-6 md:rounded-3xl md:p-10">
      {result === 'success' ? (
        <div role="status" className="py-16">
          <h2 className="text-2xl font-medium [letter-spacing:-0.03em]">
            {copy.successTitle}
          </h2>
          <p className="text-marketing-fg-muted mt-4 max-w-md text-base leading-relaxed">
            {copy.successBody}
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} aria-busy={isSubmitting}>
          <p className="text-marketing-fg-muted mb-8 text-sm">
            {copy.requiredNote}
          </p>
          <fieldset
            disabled={isSubmitting}
            className="grid min-w-0 gap-6 sm:grid-cols-2"
          >
            <ContactField
              name="firstName"
              label={copy.firstName}
              autoComplete="given-name"
              maxLength={120}
            />
            <ContactField
              name="lastName"
              label={copy.lastName}
              autoComplete="family-name"
              maxLength={120}
            />
            <ContactField
              name="email"
              label={copy.email}
              type="email"
              autoComplete="email"
              maxLength={320}
            />
            <ContactField
              name="company"
              label={copy.company}
              autoComplete="organization"
              maxLength={160}
            />
            <ContactField
              name="jobTitle"
              label={copy.jobTitle}
              autoComplete="organization-title"
              maxLength={160}
            />
            <ContactField
              name="phone"
              label={`${copy.phone} · ${copy.optional}`}
              type="tel"
              autoComplete="tel"
              maxLength={50}
              optional
            />
            <label
              htmlFor="contact-message"
              className="text-sm font-medium sm:col-span-2"
            >
              {copy.message} *
              <textarea
                id="contact-message"
                name="message"
                required
                maxLength={BUSINESS_CONTACT_MESSAGE_MAX_LENGTH}
                rows={5}
                className={fieldClass}
              />
            </label>
            <label htmlFor="contact-country" className="text-sm font-medium">
              {copy.country} *
              <div className="relative">
                <select
                  id="contact-country"
                  name="country"
                  autoComplete="country"
                  required
                  defaultValue=""
                  className={cn(fieldClass, 'pe-10')}
                >
                  <option value="" disabled>
                    {copy.select}
                  </option>
                  {countries.map((country) => (
                    <option key={country.iso} value={country.iso}>
                      {country.name}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  aria-hidden="true"
                  className="pointer-events-none absolute end-4 top-1/2 mt-1 size-4 -translate-y-1/2"
                />
              </div>
            </label>
            <label
              htmlFor="contact-marketingConsent"
              className="flex items-start gap-3 text-sm leading-relaxed sm:col-span-2"
            >
              <input
                id="contact-marketingConsent"
                name="marketingConsent"
                type="checkbox"
                className="accent-marketing-panel mt-1 size-4 shrink-0"
              />
              {copy.marketingConsent}
            </label>
          </fieldset>
          {result ? (
            <p role="alert" className="mt-6 text-sm">
              {copy[result]}
            </p>
          ) : null}
          <button
            type="submit"
            disabled={isSubmitting}
            className="group bg-marketing-panel text-marketing-panel-fg hover:bg-marketing-panel/85 focus-visible:outline-marketing-accent-strong mt-8 inline-flex min-h-12 w-full cursor-pointer items-center justify-center gap-3 rounded-md px-8 text-sm font-medium transition-colors outline-none focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-wait disabled:opacity-60"
          >
            {isSubmitting ? (
              <>
                <Loader size="sm" aria-hidden="true" />
                {copy.sending}
              </>
            ) : (
              <MarketingCtaLabel>{copy.send}</MarketingCtaLabel>
            )}
          </button>
        </form>
      )}
    </div>
  );
}

function ContactField({
  name,
  label,
  type = 'text',
  autoComplete,
  maxLength,
  optional = false,
}: {
  name: string;
  label: string;
  type?: 'text' | 'email' | 'tel';
  autoComplete?: string;
  maxLength: number;
  optional?: boolean;
}) {
  return (
    <label htmlFor={`contact-${name}`} className="text-sm font-medium">
      {label}
      {optional ? '' : ' *'}
      <input
        id={`contact-${name}`}
        name={name}
        type={type}
        autoComplete={autoComplete}
        maxLength={maxLength}
        required={!optional}
        className={fieldClass}
      />
    </label>
  );
}
